// Emptying the streaming cache for one browser, on request.
//
// Fragments, sidecars and manifests are kept in IndexedDB keyed by URL
// plus `?v=<exportVersion>`, and the whole store is dropped when a
// manifest arrives with a different version. That covers a re-export.
// It does not cover bytes rewritten UNDER THE SAME VERSION - the
// 2026-09-13 fragment rebuild put correct geometry on R2 while every
// browser that had streamed SpaceX that day kept drawing the old bytes
// from its cache, with nothing in the page able to tell.
//
//   ?cache=clear     empty the streaming cache, then load as usual
//
// One-shot and per browser: nothing is remembered, nobody else is
// affected, and the instruction is the address bar, which is what an
// owner on the phone can be told to type. window.__sl.clearCache() does
// the same from the console.
const CLEAR_VALUES = new Set(['clear', 'flush', 'reset', 'purge']);
const VERSION_KEY_PREFIX = 'bidlight.exportVersion:';

export function wantsCacheClear(search = null) {
  const query = search !== null ? search
    : (typeof window !== 'undefined' && window.location ? window.location.search : '');
  try {
    const v = new URLSearchParams(query || '').get('cache');
    return !!v && CLEAR_VALUES.has(String(v).toLowerCase());
  } catch (e) {
    return false;
  }
}

// Drops every cached blob and every remembered manifest version, and
// tells the cache to bypass IDB on the first read of each URL so a write
// that is still in flight cannot put a stale entry back.
export async function clearStreamingCache(blobCache, storage = null) {
  const store = storage !== null ? storage
    : (typeof localStorage !== 'undefined' ? localStorage : null);
  let deleted = 0;
  if (blobCache && typeof blobCache.clearAll === 'function') {
    try { deleted = await blobCache.clearAll(); } catch (e) { deleted = -1; }
    blobCache._skipNextReads = true;
  }
  let keysRemoved = 0;
  if (store) {
    try {
      const doomed = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k && k.indexOf(VERSION_KEY_PREFIX) === 0) doomed.push(k);
      }
      doomed.forEach((k) => store.removeItem(k));
      keysRemoved = doomed.length;
    } catch (e) { /* a blocked store has nothing to clear */ }
  }
  if (typeof console !== 'undefined') {
    console.log(`[bidlight] streaming cache cleared for this browser: ${deleted} blobs, ${keysRemoved} version keys.`);
  }
  return { deleted, keysRemoved };
}

export default wantsCacheClear;
